import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * A custom element that displays the cart icon and its item count.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  cartUpdateVersion = 0;

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent || '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.cartDrawerObserver = new MutationObserver(this.syncBubbleFromDrawer);
    this.cartDrawerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.syncBubbleFromDrawer();
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cartDrawerObserver?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) this.ensureCartBubbleIsCorrect();
  };

  /**
   * Handles cart updates. The drawer observer reads the final rendered count;
   * fetching cart.js here can finish out of order and overwrite it with stale data.
   * @param {CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    this.cartUpdateVersion += 1;
    if (!event.promise) return;

    Promise.resolve(event.promise).catch((error) => {
      if (error && error.name !== 'AbortError') {
        console.warn('[cart-icon] Cart update failed:', error);
      }
    });
  };

  syncBubbleFromDrawer = () => {
    const heading = document.getElementById('cart-drawer-heading');
    const match = heading && heading.textContent.match(/:\s*(\d+)\s*$/);
    if (!match) return;

    const count = parseInt(match[1], 10);
    if (count !== this.currentCartCount) this.renderCartBubble(count, false);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.currentCartCount = itemCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /** Checks the cart count on initial load and browser history restore. */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;

    const version = this.cartUpdateVersion;
    try {
      const cartRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(cartRoot + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok || version !== this.cartUpdateVersion) return;

      const cart = await response.json();
      if (version === this.cartUpdateVersion) {
        this.renderCartBubble(Number(cart.item_count) || 0, false);
      }
    } catch (error) {
      if (error && error.name !== 'AbortError') {
        console.warn('[cart-icon] Cart refresh failed:', error);
      }
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
